import { createBrowserHistory } from 'history';
import { connectRouter } from 'connected-react-router';
import { AnyAction, combineReducers, Reducer } from '@reduxjs/toolkit';
import { persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import omit from 'lodash.omit';

import { logoutSession } from './auth';
import { userLogout } from './auth/login.thunks';
import AdminDashboardReducer from './adminDashboard';
import AuthReducer from './auth';
import UsersReducer from './users';
import AccountsReducer from './accounts';
import TeamsReducer from './teams';
import { invoicesReducers } from './invoices';
import FiltersReducer from './filters';
import NotificationsReducer from './notifications';
import ApiCredentialsReducer from './apiCredentials';
import FeaturesReducer from './features';
import AccountPolicyReducer from './accountPolicy/form';
import { UpgradeAccountCombinedReducer } from './upgradeAccount';
import { SubscriptionReducer } from './customer/customer';
import SignUpReducer from './signup/signup.slices';
import IdentityProvidersReducers from './identityProviders';
import { copyTemplateReducer } from './copyTemplate/copyTemplate.slice';
import { counterReducer } from './featureToggle';
import { PackagesInfoReducer } from './packagesInfo';
import { questionPackagesReducer } from './questionPackages';

export const routerHistory = createBrowserHistory();

const logoutActionTypes = [logoutSession.type, userLogout.fulfilled.type, userLogout.rejected.type];

export const combinedReducers = combineReducers({
  router: connectRouter(routerHistory),
  adminDashboard: AdminDashboardReducer,
  auth: AuthReducer,
  users: UsersReducer,
  accounts: AccountsReducer,
  teams: TeamsReducer,
  invoices: invoicesReducers,
  filters: FiltersReducer,
  notifications: NotificationsReducer,
  apiCredentials: ApiCredentialsReducer,
  features: FeaturesReducer,
  accountPolicies: AccountPolicyReducer,
  upgradeAccount: UpgradeAccountCombinedReducer,
  subscription: SubscriptionReducer,
  signUp: SignUpReducer,
  identityProviders: IdentityProvidersReducers,
  copyTemplate: copyTemplateReducer,
  counter: counterReducer,
  packagesInfo: PackagesInfoReducer,
  questionsPackages: questionPackagesReducer
});

const rootReducer: Reducer = (state: RootState, action: AnyAction) => {
  if (action && logoutActionTypes.includes(action.type)) {
    // If the user logs out the state should be clean up
    state = undefined as unknown as RootState;
  }
  return combinedReducers(state, action);
};

const authTransform = createTransform(
  (inboundState) => inboundState,
  (outboundState: Record<string, unknown>) => {
    return omit(outboundState, ['token', 'isLoggedIn', 'loading', 'user']);
  }
);

const persistConfig = {
  key: 'primary',
  storage,
  transforms: [authTransform],
  whitelist: ['auth', 'adminDashboard', 'filters']
};

export const persistedReducer = persistReducer(persistConfig, rootReducer);

export type RootState = ReturnType<typeof combinedReducers>

export interface ThunkAPI {
  state: RootState;
}
